import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Button, Alert, Typography, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import WabaIntegrationStatus from "./WabaIntegrationStatus";import { fetchWorkspaces } from "../../../redux/Slice/Workspace/workspaceSlice";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// The "Disconnect" action lives here, not inside WabaIntegrationStatus.jsx -- that component is
// a reused template shared with other flows and stays permanently unmodified (see the plan's
// own "Must NOT touch" list). Previously there was no user-facing way to clear a workspace's
// Meta connection at all -- the only path that ever cleared it was Meta's own reactive
// PARTNER_REMOVED webhook (whatsapp.service.js's handleAccountUpdate), so switching a workspace
// from Meta to QR required creating an entirely new workspace instead of freeing up the existing
// one. This calls a new backend endpoint that reuses that exact same, already-proven clearing
// logic (clearAccessTokenAndCoexistence) rather than duplicating it. No confirmation dialog --
// matching WhatsAppQrIntegrationStatus.jsx's own Disconnect button, which uses the same
// single-click pattern for QR's equivalent action, for a consistent feel across both.
const WhatsAppDetailsDialog = ({ open, onClose, workspaceId }) => {
  const dispatch = useDispatch();
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState("");

  const handleDisconnect = async () => {
    if (!workspaceId) return;
    setDisconnectError("");
    setDisconnecting(true);
    try {
      const token = await getAuthToken();
      await axios.post(
        `${SERVER}/api/workspaces/${workspaceId}/disconnect-whatsapp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await dispatch(fetchWorkspaces());
      toast.success("WhatsApp disconnected.");
      onClose();
    } catch (err) {
      const message = err?.response?.data?.message || err.message || "Failed to disconnect WhatsApp.";
      setDisconnectError(message);
      toast.error(message);
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { bgcolor: "background.paper" } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        WhatsApp Integration Details
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <WabaIntegrationStatus />

        <Divider sx={{ my: 2 }} />

        {disconnectError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {disconnectError}
          </Alert>
        )}

        <Box>
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={disconnecting || !workspaceId}
            onClick={handleDisconnect}
            sx={{ textTransform: "none" }}
          >
            {disconnecting ? "Disconnecting…" : "Disconnect"}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Disconnects this workspace's official Meta WhatsApp connection. You can reconnect via
            Meta again afterward, or connect this same workspace via QR instead.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppDetailsDialog;
